/**
 * Queue Store - Selectors
 */

import { createSelector } from '@spcc/core'
import type { QueueState } from './queue.slice'
import {
  applyLocationFilters,
  calculateLocationStats,
  applyEntryFilters,
  sortEntriesByQueueNumber,
  locationMatchesVariant,
} from '../domain/utils'

// ============================================================================
// State Selector
// ============================================================================

interface RootState {
  queue: QueueState
}

const selectQueueState = (state: RootState) => state.queue

// ============================================================================
// Loading Location Selectors
// ============================================================================

export const selectLoadingLocations = createSelector(
  [selectQueueState],
  (state) => state.loadingLocations
)

export const selectSelectedLocation = createSelector(
  [selectQueueState],
  (state) => state.selectedLocation
)

export const selectLocationFilter = createSelector(
  [selectQueueState],
  (state) => state.locationFilter
)

export const selectLocationLoading = createSelector(
  [selectQueueState],
  (state) => state.locationLoading
)

export const selectLocationSubmitting = createSelector(
  [selectQueueState],
  (state) => state.locationSubmitting
)

export const selectLocationError = createSelector(
  [selectQueueState],
  (state) => state.locationError
)

export const selectFilteredLocations = createSelector(
  [selectLoadingLocations, selectLocationFilter],
  (locations, filter) => applyLocationFilters(locations, filter)
)

export const selectLocationStats = createSelector(
  [selectLoadingLocations],
  (locations) => calculateLocationStats(locations)
)

export const selectLocationById = createSelector(
  [selectLoadingLocations, (_state: RootState, id: string) => id],
  (locations, id) => locations.find((loc) => loc.documentId === id) || null
)

export const selectActiveLocations = createSelector(
  [selectLoadingLocations],
  (locations) => locations.filter((loc) => loc.isActive)
)

export const selectLocationsByVariant = createSelector(
  [selectLoadingLocations, (_state: RootState, variantId: string) => variantId],
  (locations, variantId) =>
    locations.filter((loc) => locationMatchesVariant(loc, variantId))
)

// ============================================================================
// Queue Entry Selectors
// ============================================================================

export const selectQueueEntries = createSelector(
  [selectQueueState],
  (state) => state.queueEntries
)

export const selectSelectedEntry = createSelector(
  [selectQueueState],
  (state) => state.selectedEntry
)

export const selectEntryFilter = createSelector(
  [selectQueueState],
  (state) => state.entryFilter
)

export const selectEntryLoading = createSelector(
  [selectQueueState],
  (state) => state.entryLoading
)

export const selectEntrySubmitting = createSelector(
  [selectQueueState],
  (state) => state.entrySubmitting
)

export const selectEntryError = createSelector(
  [selectQueueState],
  (state) => state.entryError
)

export const selectFilteredEntries = createSelector(
  [selectQueueEntries, selectEntryFilter],
  (entries, filter) => sortEntriesByQueueNumber(applyEntryFilters(entries, filter))
)

export const selectActiveEntries = createSelector(
  [selectQueueEntries],
  (entries) =>
    entries.filter((entry) =>
      ['waiting', 'ready', 'called', 'entered', 'loading'].includes(
        entry.queueStatus
      )
    )
)

export const selectExpiredEntries = createSelector(
  [selectQueueEntries],
  (entries) => entries.filter((entry) => entry.queueStatus === 'expired')
)

export const selectExpiredEntriesToday = createSelector(
  [selectQueueState],
  (state) => state.expiredEntriesToday
)

export const selectExpiredEntriesLoading = createSelector(
  [selectQueueState],
  (state) => state.expiredEntriesLoading
)

export const selectEntriesByLocation = createSelector(
  [selectQueueEntries, (_state: RootState, locationId: string) => locationId],
  (entries, locationId) =>
    sortEntriesByQueueNumber(
      entries.filter((entry) => entry.loadingLocation?.documentId === locationId)
    )
)

export const selectEntryById = createSelector(
  [selectQueueEntries, (_state: RootState, id: string) => id],
  (entries, id) => entries.find((entry) => entry.documentId === id) || null
)

// ============================================================================
// Join Queue Selectors
// ============================================================================

export const selectWaitTimes = createSelector(
  [selectQueueState],
  (state) => state.waitTimes
)

export const selectJoinResult = createSelector(
  [selectQueueState],
  (state) => state.joinResult
)

export const selectJoinExistingEntry = createSelector(
  [selectQueueState],
  (state) => state.joinExistingEntry
)

export const selectJoinLoading = createSelector(
  [selectQueueState],
  (state) => state.joinLoading
)

export const selectJoinError = createSelector(
  [selectQueueState],
  (state) => state.joinError
)

// ============================================================================
// Dashboard Selectors
// ============================================================================

export const selectDashboard = createSelector(
  [selectQueueState],
  (state) => state.dashboard
)

export const selectDashboardLoading = createSelector(
  [selectQueueState],
  (state) => state.dashboardLoading
)

export const selectDashboardError = createSelector(
  [selectQueueState],
  (state) => state.dashboardError
)

// ============================================================================
// Events Selectors
// ============================================================================

export const selectRecentEvents = createSelector(
  [selectQueueState],
  (state) => state.recentEvents
)

export const selectEventsLoading = createSelector(
  [selectQueueState],
  (state) => state.eventsLoading
)

export const selectEventsError = createSelector(
  [selectQueueState],
  (state) => state.eventsError
)

// ============================================================================
// Computed Stats Selectors
// ============================================================================

export const selectQueueStats = createSelector(
  [selectActiveEntries, selectActiveLocations],
  (entries, locations) => ({
    totalLocations: locations.length,
    totalCapacity: locations.reduce((sum, loc) => sum + loc.maxVehicles, 0),
    totalInQueue: entries.length,
    waiting: entries.filter((e) => e.queueStatus === 'waiting').length,
    ready: entries.filter((e) =>
      ['ready', 'called'].includes(e.queueStatus)
    ).length,
    inside: entries.filter((e) =>
      ['entered', 'loading'].includes(e.queueStatus)
    ).length,
  })
)

export const selectLocationQueueStats = createSelector(
  [
    selectLoadingLocations,
    selectActiveEntries,
    (_state: RootState, locationId: string) => locationId,
  ],
  (locations, entries, locationId) => {
    const location = locations.find((loc) => loc.documentId === locationId)
    if (!location) return null

    const locationEntries = entries.filter(
      (e) => e.loadingLocation?.documentId === locationId
    )
    const activeInside = locationEntries.filter((e) =>
      ['entered', 'loading'].includes(e.queueStatus)
    ).length

    return {
      location,
      queueLength: locationEntries.length,
      activeInside,
      waiting: locationEntries.filter((e) =>
        ['waiting', 'ready', 'called'].includes(e.queueStatus)
      ).length,
      availableSlots: Math.max(0, location.maxVehicles - activeInside),
      isFull: activeInside >= location.maxVehicles,
    }
  }
)

// Reference data selectors
export const selectVariants     = (state: { queue: QueueState }) => state.queue.variants
export const selectFactories    = (state: { queue: QueueState }) => state.queue.factories
export const selectRefDataLoading = (state: { queue: QueueState }) => state.queue.referenceDataLoading

// ============================================================================
// Loading Location Rule Selectors
// ============================================================================

export const selectLoadingLocationRules = createSelector(
  [selectQueueState],
  (state) => state.loadingLocationRules
)

export const selectSelectedRule = createSelector(
  [selectQueueState],
  (state) => state.selectedRule
)

export const selectRuleFilter = createSelector(
  [selectQueueState],
  (state) => state.ruleFilter
)

export const selectRuleLoading = createSelector(
  [selectQueueState],
  (state) => state.ruleLoading
)

export const selectRuleSubmitting = createSelector(
  [selectQueueState],
  (state) => state.ruleSubmitting
)

export const selectRuleError = createSelector(
  [selectQueueState],
  (state) => state.ruleError
)

export const selectRuleCities = createSelector(
  [selectQueueState],
  (state) => state.cities
)

export const selectRuleTenants = createSelector(
  [selectQueueState],
  (state) => state.tenants
)

export const selectRuleReferenceDataLoading = createSelector(
  [selectQueueState],
  (state) => state.ruleReferenceDataLoading
)
